import React, { useEffect, useRef, useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { HealthQuestionsSchema } from '@features/onboarding/schema/onboarding';
import { STEP_PHQ_1_ERRORS } from 'src/features/onboarding/components/6-health-question-form/constants';
import { HealthQuestion } from '@features/onboarding/components/6-health-question-form/interfaces';
import { RadioButton } from '@components/shared/radio-button/RadioButton';
import { FormField } from '@components/ui/form';
import { FormMessageSpan } from '@components/ui/form';
import { cn } from '@utils/index';
import { useHealthStore } from 'src/store/health';

interface InitialQuestionsStepProps {
  question: HealthQuestion;
  form: UseFormReturn<HealthQuestionsSchema>;
  errorMessage?: string | null;
}

export const InitialQuestionsStep: React.FC<InitialQuestionsStepProps> = ({
  question,
  form,
}) => {
  const { isFailValidation } = useHealthStore();
  const errorRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    form.trigger('healthQuestions.initialQuestions');
  }, [form])

  // Auto-scroll to error when validation fails
  useEffect(() => {
    if (isFailValidation && errorRef.current) {
      console.log('Scrolling to error in InitialQuestionsStep');
      setTimeout(() => {
        smoothScrollTo(errorRef.current!);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFailValidation]);

  // Easing function for smooth scrolling
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  // Smooth scroll function
  const smoothScrollTo = useCallback((element: HTMLElement, duration: number = 800) => {
    // Check screen width to decide scrolling method
    const isLargeScreen = window.innerWidth >= 1400;
    const scrollContainer = isLargeScreen ? document.getElementById('childrenBlock') : null;
    
    const getScrollPosition = (elem: HTMLElement, container?: HTMLElement | null) => {
      let top = 0;
      let current = elem;
      while (current && (!container || current !== container)) {
        top += current.offsetTop;
        current = current.offsetParent as HTMLElement;
      }
      return top - (container ? container.offsetTop : 0) - (isLargeScreen ? 100 : 100);
    };
    
    const start = scrollContainer ? scrollContainer.scrollTop : window.pageYOffset;
    const target = getScrollPosition(element, scrollContainer);
    const distance = target - start;
    let startTime: number | null = null;
    
    const animation = (currentTime: number) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      const easeProgress = easeInOutCubic(progress);
      
      if (scrollContainer) {
        scrollContainer.scrollTop = start + distance * easeProgress;
      } else {
        window.scrollTo(0, start + distance * easeProgress);
      }
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  }, []);

  return (
    <div className="flex flex-col gap-5 lg:gap-10 w-full">
      {question.subQuestions?.map((subQuestion, index) => {
        // Build typed field path for react-hook-form
        type QuestionKey = 'treatment' | 'invalid' | 'pregnancy';
        const questionMap: QuestionKey[] = ['treatment', 'invalid', 'pregnancy'];
        const fieldName = `healthQuestions.initialQuestions.${questionMap[index]}` as const;
        const isTreatmentQuestion = questionMap[index] === 'treatment';

        // Retrieve possible validation error for this field in a type-safe way
        const error = (
          (form.formState.errors as Partial<Record<string, any>>) ?? {}
        ).healthQuestions?.initialQuestions?.[
          questionMap[index] as keyof typeof STEP_PHQ_1_ERRORS
        ];

        return (
          <div
            key={index}
            className={cn(
              'flex relative gap-20 max-xl:gap-8.5 p-6 items-center border border-transparent justify-between flex-col max-xl:text-center card-shadow bg-card',
            )}
          >
            <div className="w-full">
              <div className="flex max-xl:flex-col max-xl:items-center justify-between w-full md:gap-2">
                <p className="!font-secondary text-xl max-md:text-[15px] mb-2 max-w-135 max-md:px-2.5 max-md:max-w-87.5">
                  {subQuestion}
                </p>
                <div className="flex flex-col gap-2 max-xl:flex-row max-xl:gap-2.5">
                  <FormField
                    control={form.control}
                    name={fieldName as any}
                    render={({ field }) => (
                       <>
                      <div
                        className={cn(
                          'radio-wrapper-date max-md:flex max-md:h-13.5',
                          field.value === true && 'radio-wrapper-checked'
                        )}
                      >
                        <RadioButton
                          label="YES"
                          name={`initialQuestions_${questionMap[index]}`}
                          value="true"
                          labelProps={{
                            className: 'text-black xl:text-base text-sm shrink-0',
                          }}
                          checked={field.value === true}
                          onChange={() => field.onChange(true)}
                        />
                      </div>

                      <div
                        className={cn(
                          'max-w-23.5 max-md:flex max-md:h-13.5',
                          'radio-wrapper-date',
                          field.value === false && 'radio-wrapper-checked'
                        )}
                      >
                        <RadioButton
                          label="NO"
                          name={`initialQuestions_${questionMap[index]}`}
                          value="false"
                          labelProps={{
                            className: 'text-black xl:text-base text-sm shrink-0',
                          }}
                          checked={field.value === false}
                          onChange={() => {
                            field.onChange(false);
                            if (isTreatmentQuestion) {
                              // Reset OtherHealthConditions to default (nothing selected)
                              try {
                                form.setValue(
                                  'healthQuestions.otherHealthConditions.conditions' as any,
                                  [],
                                  { shouldDirty: true, shouldValidate: true }
                                );
                                form.setValue(
                                  'healthQuestions.otherHealthConditions.details' as any,
                                  {},
                                  { shouldDirty: true, shouldValidate: true }
                                );
                              } catch {}
                            }
                          }}
                        />
                      </div>
                    </>
                    )}
                  />
                </div>
              </div>
              {error && isFailValidation && (
                <p 
                  ref={errorRef}
                  className="mt-4 text-center"
                >
                  <FormMessageSpan
                    className="text-error font-semibold xl:text-left"
                    text={error.message}
                  />
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
